/**
 * Stand worksheet actions renderer
 */
import {
  AbstractType,
  Component,
  InjectionToken,
  Injector,
  Type,
  ViewEncapsulation,
} from '@angular/core';
import { ICellRendererParams } from 'ag-grid-community';
import { AgRendererComponent } from 'ag-grid-angular';
import { ActionButton, ENTITY_TYPES, COMMON_ACTION_TYPES } from '@shared/types';
import { Router } from '@angular/router';
import { cloneDeep, forEach, get, includes, isEqual, isFunction } from 'lodash';
import {
  SitesService,
  StandWorksheetService,
  StateService,
  XprtDataService,
} from '@shared/services';
import { APIActions, ToolsActions, UiActions } from '@store/actions';
import { lazyInject } from '@containers/base/decorators/lazy-inject.decorator';
import { STAND_WORKSHEET_ACTION_BUTTONS } from '@shared/types/entities/stand-worksheet/stand-worksheet-action-buttons.enum';
import { STAND_WORKSHEET_STATUS_VALUE } from '@shared/types/enums/stand-worksheet-status.enum';
import { IAppState } from '@app/store';
import { NgRedux, select } from '@angular-redux/store';
import { getSingularEntityName } from '@shared/configs';
import { StandWorksheetFormService } from '@app/shared/services/stand-worksheet/stand-worksheet.form.service';
import { EVENT_STATUS_VALUE } from '@app/shared/types/enums/event-status.enum';
import { InventoryItemVenueService } from '@app/shared/services/inventory-item-venue';
import { StandWorksheetStatusChangeService } from '@app/shared/services/stand-worksheet/stand-worksheet-status-change.service';
import { buildSWSPdfConfig } from '@app/shared/services/stand-worksheet/stand-worksheet-pdf-config';
import { StandWorksheetPrintPdfService } from '@app/shared/services/stand-worksheet/stand-worksheet-print-pdf.service';
import {
  PdfGenerateServiceV2,
  IPdfDefinitions,
  PdfSelectColumn,
} from '@app/shared/common/make-pdf/v2';
import { Observable } from 'rxjs';
import { STAND_WORKSHEET_PDF_SELECTED_COLUMNS } from '@app/shared/services/stand-worksheet/stand-worksheet.constants';
import { getObservableValue } from '@app/shared/utils';
import { XVII18nService } from '@app/i18n/services';
import { I18n } from '@app/i18n/strings-keys';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xvi-transfer-actions-renderer',
  templateUrl: 'stand-worksheet-actions-renderer.component.html',
  styleUrls: ['stand-worksheet-actions-renderer.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class XVIStandWorksheetRendererComponent implements AgRendererComponent {
  @select(['ui', STAND_WORKSHEET_PDF_SELECTED_COLUMNS])
  public printColumns$: Observable<PdfSelectColumn[]>;
  @lazyInject(ToolsActions) public toolsActions: ToolsActions;
  @lazyInject(PdfGenerateServiceV2)
  public pdfGenerateService: PdfGenerateServiceV2;
  @lazyInject(Router) public router: Router;
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(StandWorksheetService)
  public standWorksheetService: StandWorksheetService;
  @lazyInject(StandWorksheetPrintPdfService)
  public standWorksheetPdfService: StandWorksheetPrintPdfService;
  @lazyInject(StateService) public stateService: StateService;
  @lazyInject(APIActions) public apiActions: APIActions;
  @lazyInject(NgRedux) public ngRedux: NgRedux<IAppState>;
  @lazyInject(SitesService) public sitesService: SitesService;
  @lazyInject(XprtDataService) public xprtDataService: XprtDataService;
  @lazyInject(StandWorksheetFormService)
  public standWorksheetFormService: StandWorksheetFormService;
  @lazyInject(InventoryItemVenueService)
  public inventoryItemVenueService: InventoryItemVenueService;
  @lazyInject(StandWorksheetStatusChangeService)
  public standWorksheetStatusChangeService: StandWorksheetStatusChangeService;
  @lazyInject(XVII18nService)
  public i18nService: XVII18nService;

  public data: any;
  public needGetUpdateItemsStatus: string[] = ['Open'];
  public actionButtons: ActionButton[];

  public constructor(public injector: Injector) {}

  public agInit(params: ICellRendererParams): void {
    this.data = params.node.data;
    this.initActionButtons();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public refresh(params: any): boolean {
    const defaultRequestParams = {
      query: {},
      options: {},
    };
    const entityType = this.standWorksheetService.config.ENTITY_TYPE;
    const previousRequest = this.stateService.getByPath(
      ['entities', entityType, 'previousRequest'],
      defaultRequestParams,
    );

    this.apiActions.fetchList(
      entityType,
      previousRequest.query,
      previousRequest.options,
    );

    return true;
  }

  public edit(): void {
    const status = this.data?.status;

    this.router.navigate(
      [this.standWorksheetService.config.uiRoute, get(this, 'data._id', '')],
      {
        queryParams: {
          get_updated_items: includes(this.needGetUpdateItemsStatus, status)
            ? true
            : null,
        },
      },
    );
  }

  public async reopen(): Promise<void> {
    const message = this.i18nService.instant(
      I18n.ConfirmMessages.ConfirmActionToEntity,
      {
        action: this.i18nService.translate(I18n.Common.reopen),
        entityType: this.i18nService.translate(
          getSingularEntityName(ENTITY_TYPES.STAND_WORKSHEET),
        ),
      },
    );
    const result = await this.uiActions.confirm({
      text: message,
    });
    if (!result.confirmed) {
      return;
    }

    const isAvailable =
      await this.standWorksheetStatusChangeService.isSWStatusChangeAvailable(
        this.data._id,
        this.data.status,
      );
    if (isAvailable) {
      await this.changeStandWorksheetStatusById(
        STAND_WORKSHEET_STATUS_VALUE.Open,
      );
    } else {
      this.refresh({});
    }
  }

  public async changeStandWorksheetStatusById(
    status: STAND_WORKSHEET_STATUS_VALUE,
  ): Promise<void> {
    await this.apiActions.updateEntityAsPromised(
      ENTITY_TYPES.STAND_WORKSHEET,
      { status },
      {
        endpoint: this.data._id,
        requiredToSendFields: ['status'],
        successNotificationMessage: {
          message: this.i18nService.instant(I18n.InfoMessages.Didsuccessfully, {
            did: this.i18nService.translate(I18n.Status.Reopened),
          }),
        },
      },
    );
    this.refresh({});
  }

  public getPdfConfig(): IPdfDefinitions {
    const pdfDataPromise = this.standWorksheetService.getPdfData(this.data);
    const isOffPostClosureSetting =
      !this.stateService.getStandWorksheetPostClosureSetting();
    const entryFormHeaders =
      this.standWorksheetPdfService.getPDFHeadersByPermission();

    return buildSWSPdfConfig(() => pdfDataPromise, {
      entryFormHeaders,
      isOffPostClosureSetting,
      uiActions: this.uiActions,
      pdfSelectColumn: getObservableValue(this.printColumns$),
      i18nService: this.i18nService,
    });
  }

  public async print(): Promise<void> {
    const pdfConfig = this.getPdfConfig();

    await this.pdfGenerateService.printPdfAsync(pdfConfig);
  }

  public async export(): Promise<void> {
    const pdfConfig = this.getPdfConfig();

    await this.pdfGenerateService.downloadPdfAsync(pdfConfig);
  }

  public emitAction(type: string): void {
    switch (type) {
      case COMMON_ACTION_TYPES.View:
      case COMMON_ACTION_TYPES.Edit:
        this.edit();
        break;
      case COMMON_ACTION_TYPES.Reopen:
        this.reopen();
        break;
      case COMMON_ACTION_TYPES.Print:
        this.print();
        break;
      case COMMON_ACTION_TYPES.Export:
        this.export();
        break;
      default:
        break;
    }
  }

  public async changeTransferStatusById(
    status: STAND_WORKSHEET_STATUS_VALUE,
  ): Promise<void> {
    await this.apiActions.updateEntityAsPromised(
      ENTITY_TYPES.STAND_WORKSHEET,
      { status },
      { endpoint: this.data._id },
    );
    this.refresh({});
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  private initActionButtons(): void {
    this.actionButtons = cloneDeep(
      STAND_WORKSHEET_ACTION_BUTTONS,
    ) as ActionButton[];
    const isClosedEventStatus = isEqual(
      this.standWorksheetService.selectedEventStatus,
      EVENT_STATUS_VALUE.Closed,
    );
    forEach(this.actionButtons, (btn) => {
      switch (btn.type) {
        case COMMON_ACTION_TYPES.View:
          btn.visible = includes(
            [
              STAND_WORKSHEET_STATUS_VALUE.Approved,
              STAND_WORKSHEET_STATUS_VALUE.Completed,
              STAND_WORKSHEET_STATUS_VALUE.Canceled,
            ],
            this.data.status,
          );
          break;
        case COMMON_ACTION_TYPES.Edit:
          btn.visible = STAND_WORKSHEET_STATUS_VALUE.Open === this.data.status;
          break;
        case COMMON_ACTION_TYPES.Reopen:
          btn.visible =
            includes(
              [
                STAND_WORKSHEET_STATUS_VALUE.Approved,
                STAND_WORKSHEET_STATUS_VALUE.Canceled,
              ],
              this.data.status,
            ) && !isClosedEventStatus;
          break;
        case COMMON_ACTION_TYPES.Print:
        case COMMON_ACTION_TYPES.Export:
          btn.visible = includes(
            [
              STAND_WORKSHEET_STATUS_VALUE.Open,
              STAND_WORKSHEET_STATUS_VALUE.Approved,
              STAND_WORKSHEET_STATUS_VALUE.Completed,
              STAND_WORKSHEET_STATUS_VALUE.Canceled,
            ],
            this.data.status,
          );
          break;
        default:
          break;
      }
    });
  }
}
